import FileSaver from 'file-saver';
import Moment from 'moment';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import i18n from 'src/i18n';

export enum AuditLogType {
  UserLogin = 'UserLogin',
  UserAccessOrganization = 'UserAccessOrganization',
  UserLogout = 'UserLogout',
  UserAddedToGroup = 'UserAddedToGroup',
  UserRemovedFromGroup = 'UserRemovedFromGroup',
  RoleAssignedToUser = 'RoleAssignedToUser',
  RoleUnassignedToUser = 'RoleUnassignedToUser',
  RoleAssignedToGroup = 'RoleAssignedToGroup',
  RoleUnassignedToGroup = 'RoleUnassignedToGroup',
  InvitationSent = 'InvitationSent',
  InvitationAccepted = 'InvitationAccepted',
  BackupSuccess = 'BackupSuccess',
  BackupError = 'BackupError',
  RestoreBackupStart = 'RestoreBackupStart',
  BackupStart = 'BackupStart',
  RestoreBackupSuccess = 'RestoreBackupSuccess',
  RestoreBackupError = 'RestoreBackupError',
  RoleExpirationDateChanged = 'RoleExpirationDateChanged',
}

export const AuditLogOptions = [
  { label: 'UserLogin', value: 'UserLogin' },
  { label: 'UserAccessOrganization', value: 'UserAccessOrganization' },
  { label: 'UserLogout', value: 'UserLogout' },
  { label: 'UserAddedToGroup', value: 'UserAddedToGroup' },
  { label: 'UserRemovedFromGroup', value: 'UserRemovedFromGroup' },
  { label: 'RoleAssignedToUser', value: 'RoleAssignedToUser' },
  { label: 'RoleUnassignedToUser', value: 'RoleUnassignedToUser' },
  { label: 'RoleAssignedToGroup', value: 'RoleAssignedToGroup' },
  { label: 'RoleUnassignedToGroup', value: 'RoleUnassignedToGroup' },
  { label: 'InvitationSent', value: 'InvitationSent' },
  { label: 'InvitationAccepted', value: 'InvitationAccepted' },
  { label: 'BackupSuccess', value: 'BackupSuccess' },
  { label: 'BackupError', value: 'BackupError' },
  { label: 'RestoreBackupSuccess', value: 'RestoreBackupSuccess' },
  { label: 'RestoreBackupError', value: 'RestoreBackupError' },
  { label: 'RoleExpirationDateChanged', value: 'RoleExpirationDateChanged' },
  { label: 'UnblockStructmasterLicense', value: 'UnblockStructmasterLicense' },
];

export interface AuditLogSummary {
  id: string;
  timestamp: string;

  userId: string;
  userDisplayName: string;

  type: AuditLogType;

  organizationId?: string;
  organizationDisplayName: string;

  relatedEntityId: string;
  relatedEntityTitle: string;

  description: string;
}

export class AuditLogSummaryStore extends DataStore<AuditLogSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/audit-logs`, []);
  }

  public async exportCsv(query: Query) {
    const qs = DataStore.buildUrl(query);

    return await this.handleCallAsync(async () => {
      const result = await this.httpService.get<Blob>(`${this.baseUrl}/csv?${qs}&utcOffset=${Moment().utcOffset()}`, {
        responseType: 'blob',
      });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, `logs [${Moment().format('DD-MM-YYYY, HH.mm.ss')}].csv`);

      return true;
    });
  }

  public async getLogs(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      try {
        const response = await this.httpService.get<QueryResult<AuditLogSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
        return response.data.items;
      } catch {
        return [];
      }
    });
  }
}
